import React, { useLayoutEffect, useRef, useState } from "react";
import { Box, Button } from "@mui/material";

// One mounted list, one authoritative start, continuous elapsed-time motion.
export default function WinnerSequence({ children, count, startedAt, durationSeconds, serverOffset = 0,
  replayKey = 0, onComplete, sx = {} }) {
  const viewport = useRef(null), track = useRef(null), completion = useRef(onComplete);
  const [staticList, setStaticList] = useState(false);
  const [finished, setFinished] = useState(false);
  const offset = useRef(serverOffset); offset.current = serverOffset; completion.current = onComplete;
  useLayoutEffect(() => {
    const box=viewport.current, content=track.current;
    if(!box || !content || !count) return undefined;
    const reduced=window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const start=replayKey>0?Date.now()+offset.current:Date.parse(startedAt);
    const duration=Math.max(18,Number(durationSeconds)||0,count*5)*1000;
    const elapsed=Number.isFinite(start)?Math.max(0,Date.now()+offset.current-start):duration;
    let frame, disposed=false, done=false, manual=false;
    const complete=()=>{if(!done){done=true;completion.current?.();}};
    const showList=()=>{manual=true;content.style.transform='none';setStaticList(true);};
    const origin=performance.now()-elapsed;
    setFinished(false);
    setStaticList(Boolean(reduced?.matches || elapsed>=duration));
    content.style.transform='none'; box.scrollTop=0;
    if(elapsed>=duration){complete();return undefined;}
    const tick=()=>{
      if(disposed)return;
      const progress=Math.min(1,Math.max(0,(performance.now()-origin)/duration));
      if(!manual && !reduced?.matches) {
        // No per-row jumps, React updates, or list reconstruction on animation frames.
        const travel=box.clientHeight+content.offsetHeight;
        const ramp=Math.min(0.03,500/duration);
        const distance=progress<ramp?progress*progress/(2*ramp*(1-ramp))
          :progress>1-ramp?1-(1-progress)*(1-progress)/(2*ramp*(1-ramp))
          :(progress-ramp/2)/(1-ramp);
        content.style.transform=`translate3d(0,${box.clientHeight-travel*distance}px,0)`;
      }
      if(progress>=1){complete();setFinished(true);return;}
      frame=requestAnimationFrame(tick);
    };
    const onMotionChange=()=>{if(reduced.matches)showList();};
    const manualInput=()=>showList();
    for(const name of ['wheel','touchstart','keydown']) box.addEventListener(name,manualInput,{passive:true});
    reduced?.addEventListener?.('change',onMotionChange);
    tick();
    return ()=>{disposed=true;cancelAnimationFrame(frame);for(const name of ['wheel','touchstart','keydown'])box.removeEventListener(name,manualInput);reduced?.removeEventListener?.('change',onMotionChange);};
  }, [count,startedAt,durationSeconds,replayKey]);
  return <Box ref={viewport} role="region" aria-label="All draw winners" tabIndex={0}
    sx={{width:'100%',height:{xs:'60svh',sm:560},minHeight:240,maxHeight:'70vh',
      overflowY:staticList?'auto':'hidden',overflowX:'hidden',overscrollBehavior:'contain',position:'relative',...sx}}>
    {finished && !staticList && <Button onClick={()=>{track.current.style.transform='none';setStaticList(true);}} sx={{position:'absolute',top:16,left:'50%',transform:'translateX(-50%)',zIndex:1,color:'#ffcc33'}}>Presentation complete — View all winners</Button>}
    <Box ref={track} data-winner-track sx={{willChange:staticList?'auto':'transform',py:2}}>{children}</Box>
  </Box>;
}
